/**
 * @fileoverview Special Piece Manager - 特殊ピース生成・管理システム
 * 責務：特殊ピース生成、追加、検索、マッチ処理
 */

import { generateSpecialPiece } from './system.js';

/**
 * 特殊ピースシステムの初期化
 */
export function initializeSpecialPieces() {
  // 初期化処理は不要（関数ベースのため）
}

/**
 * 特殊ピース生成ルールを適用
 * @param {Object} board - Boardインスタンス
 * @param {Array} matches - マッチ配列
 * @param {Array|null} swapedPieces - スワップされたピース
 * @returns {Promise<Array>} 更新されたマッチ配列
 */
export async function applySpecialPieceRules(board, matches, swapedPieces = null) {
  if (!Array.isArray(matches) || matches.length === 0) {
    return [];
  }

  const combinedMatches = combineOverlappingMatches(matches);
  if (combinedMatches.length === 0) {
    return [];
  }

  const preferredCoords = Array.isArray(swapedPieces)
    ? swapedPieces.filter(Boolean).map((piece) => [...piece.position])
    : [];

  const matchPlans = combinedMatches
    .map((match) => {
      if (!Array.isArray(match) || match.length === 0) {
        return null;
      }

      const coord = getSpecialPieceCoord(match, { preferredCoords });
      return {
        coord,
        match,
      };
    })
    .filter(Boolean);

  const updatedMatches = await Promise.all(
    matchPlans.map(async ({ coord, match }) => {
      const piece = board.getPiece(coord.row, coord.col);
      return piece ? await checkAndAddSpecial(board, piece, match) : match;
    }),
  );

  return updatedMatches.flat();
}

/**
 * 特殊ピースの生成チェックと予約
 * @param {Object} board - Boardインスタンス
 * @param {Object} piece - 対象ピース
 * @param {Array} match - マッチ配列
 * @returns {Promise<Array>} 更新されたマッチ配列
 */
async function checkAndAddSpecial(board, piece, match) {
  const specialPiece = generateSpecialPiece(match);
  if (specialPiece) {
    // 特殊ピース生成を予約（Board更新はしない）
    const matchedColors = Array.isArray(match)
      ? match.map((m) => m && m.color).filter(Boolean)
      : [];

    // ピースに生成予約メタデータを付与
    piece._pendingCreation = {
      specialPiece,
      matchedColors,
    };
    // ★重要：matchから削除しない（削除アニメーションを実行するため）
  }
  return match;
}

/**
 * 特殊ピース検索
 * @param {Object} board - Boardインスタンス
 * @param {Array} specialTypes - 検索する特殊ピースタイプ配列
 * @returns {Array} 見つかった特殊ピース配列
 */
export function findSpecialPiece(board, specialTypes) {
  if (!board || typeof board.getSpecialPieces !== 'function') {
    return [];
  }
  return board.getSpecialPieces(specialTypes).map((result) => result.piece);
}

// =============================================================================
// ユーティリティ関数群
// =============================================================================

/**
 * 重複するマッチを結合（簡素化版）
 * @param {Array} matches - マッチ配列
 * @returns {Array} 結合されたマッチ配列
 */
function combineOverlappingMatches(matches) {
  if (!Array.isArray(matches) || matches.length === 0) {
    return [];
  }

  const result = [];
  const processed = new Set();

  for (let i = 0; i < matches.length; i++) {
    if (processed.has(i)) continue;

    let combined = [...matches[i]];
    processed.add(i);

    // 重複するマッチを探して結合
    for (let j = i + 1; j < matches.length; j++) {
      if (processed.has(j)) continue;

      const hasOverlap = matches[j].some((piece) => combined.includes(piece));
      if (hasOverlap) {
        combined = [...new Set([...combined, ...matches[j]])];
        processed.add(j);
      }
    }
    result.push(combined);
  }
  return result;
}

/**
 * 特殊ピース配置座標を取得
 * @param {Array} match - マッチ配列
 * @returns {Object} 座標 {row, col}
 */
function getSpecialPieceCoord(match, { preferredCoords = [] } = {}) {
  if (!Array.isArray(match) || match.length === 0) {
    return { row: 0, col: 0 };
  }

  if (match.some((piece) => !piece || !piece.position)) {
    throw new Error('Invalid match data');
  }

  const pieceIndex = buildPieceIndex(match);
  const intersectionCandidates = match.filter((piece) => {
    const [row, col] = piece.position;
    const horizontal =
      countDirectionalPieces(pieceIndex, row, col, 0, -1) +
      1 +
      countDirectionalPieces(pieceIndex, row, col, 0, 1);
    const vertical =
      countDirectionalPieces(pieceIndex, row, col, -1, 0) +
      1 +
      countDirectionalPieces(pieceIndex, row, col, 1, 0);
    return horizontal >= 3 && vertical >= 3;
  });

  let targetPiece = null;

  if (intersectionCandidates.length > 0) {
    targetPiece =
      selectPreferredPiece(intersectionCandidates, preferredCoords) ||
      getCenterPreferredPiece(intersectionCandidates);
  } else {
    targetPiece = getCenterPreferredPiece(match);
  }

  return {
    row: targetPiece.position[0],
    col: targetPiece.position[1],
  };
}

function buildPieceIndex(pieces) {
  const index = new Map();
  for (const piece of pieces) {
    const [row, col] = piece.position;
    index.set(`${row}:${col}`, piece);
  }
  return index;
}

function countDirectionalPieces(index, row, col, dRow, dCol) {
  let count = 0;
  let currentRow = row + dRow;
  let currentCol = col + dCol;

  while (index.has(`${currentRow}:${currentCol}`)) {
    count += 1;
    currentRow += dRow;
    currentCol += dCol;
  }

  return count;
}

function selectPreferredPiece(candidates, preferredCoords) {
  if (!Array.isArray(preferredCoords) || preferredCoords.length === 0) {
    return null;
  }

  let bestPiece = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const piece of candidates) {
    const [row, col] = piece.position;
    for (const [pRow, pCol] of preferredCoords) {
      const distance = Math.abs(row - pRow) + Math.abs(col - pCol);
      if (distance < bestDistance) {
        bestPiece = piece;
        bestDistance = distance;
      }
    }
  }

  return bestPiece;
}

function getCenterPreferredPiece(pieces) {
  const rows = pieces.map((p) => p.position[0]);
  const cols = pieces.map((p) => p.position[1]);
  const centerRow = (Math.min(...rows) + Math.max(...rows)) / 2;
  const centerCol = (Math.min(...cols) + Math.max(...cols)) / 2;

  return pieces.reduce((closest, piece) => {
    const [row, col] = piece.position;
    const distance = Math.abs(row - centerRow) + Math.abs(col - centerCol);
    const closestDistance =
      Math.abs(closest.position[0] - centerRow) + Math.abs(closest.position[1] - centerCol);
    return distance < closestDistance ? piece : closest;
  });
}

/**
 * 特殊ピース作成アニメーションの完了を待機
 * @returns {Promise<void>} 全ての特殊ピース作成が完了した時に解決されるPromise
 */
export function waitForSpecialCreations() {
  // 現在は解決済みのPromiseを返す
  return Promise.resolve();
}
